import React from "react";
import { Helmet } from "react-helmet";
import TechIntro from "../ReusableComponents/TechIntro";
import TechWhyChoose from "../ReusableComponents/TechWhyChoose";
import TechRoles from "../ReusableComponents/TechRoles";
import TechStaffingServices from "../ReusableComponents/TechStaffingServices";
import TechRecruitingAgency from "../ReusableComponents/TechRecruitingAgency";
import TechOutcomes from "../ReusableComponents/TechOutcomes";
import TechCTA from "../ReusableComponents/TechCTA";

const cloudRoleGroups = [
  {
    title: 'Architecture & Engineering',
    items: [
      { name: 'Cloud Solutions Architect', icon: '🏗️' },
      { name: 'Cloud Engineer (AWS/Azure/GCP)', icon: '☁️' },
      { name: 'DevOps Engineer', icon: '⚡' },
      { name: 'Site Reliability Engineer (SRE)', icon: '🔄' },
    ]
  },
  {
    title: 'Platform & Security',
    items: [
      { name: 'Platform Engineer', icon: '🖥️' },
      { name: 'Cloud Security Engineer', icon: '🔒' },
      { name: 'FinOps Analyst', icon: '💰' },
      { name: 'Kubernetes / Container Engineer', icon: '🐳' },
    ]
  },
  {
    title: 'Data & Automation',
    items: [
      { name: 'Data Engineer (Cloud)', icon: '🗄️' },
      { name: 'Cloud Network Engineer', icon: '🌐' },
      { name: 'IaC Engineer (Terraform/Ansible)', icon: '📦' },
      { name: 'Cloud Migration Specialist', icon: '🚀' },
    ]
  }
];

const cloudFeatures = [
  {
    icon: '✦',
    title: 'Multi-Cloud Platform Expertise',
    text: 'Our recruiters are trained across AWS, Azure, and GCP platforms. We assess candidates on real cloud architectures — not just certification badges — ensuring every hire is genuinely capable of delivering in your environment.',
  },
  {
    icon: '✔',
    title: 'Rigorous Cloud Vetting',
    text: 'All cloud consultants are verified for: AWS/Azure/GCP certifications and hands-on experience, infrastructure-as-code proficiency (Terraform, CloudFormation, Bicep), container and orchestration skills (Docker, Kubernetes, EKS/AKS/GKE), and cloud security and governance knowledge.',
  },
  {
    icon: '⚙',
    title: 'Flexible Engagement Models',
    text: 'Contract, contract-to-hire, or permanent. From an individual cloud engineer to a full cloud center of excellence — we structure engagements to match your project scope, timeline, and budget.',
  },
  {
    icon: '🛡',
    title: 'FinOps & Cloud Governance Alignment',
    text: 'We match candidates with experience in cloud cost management, tagging strategies, landing zone design, and enterprise governance frameworks — critical for large-scale cloud programs.',
  },
];

const cloudProblemItems = [
  'Cloud migrations derailed by engineers unfamiliar with enterprise-scale lift-and-shift or re-architecting.',
  'Security misconfigurations from consultants lacking cloud security and governance expertise.',
  'Ballooning cloud bills due to architects without FinOps or cost optimization experience.',
  'Missed modernization timelines as projects stall waiting for the right Kubernetes or serverless expertise.',
  'Technical debt accumulation from poorly architected cloud environments that require costly rework.',
];

const cloudSolutionPoints = [
  'Specialized cloud recruiters with deep AWS, Azure, and GCP expertise',
  'Founders and entrepreneurs who have built and scaled cloud-native platforms',
  'Cloud architects and engineers with a passion for infrastructure as code and DevOps',
];

const cloudOutcomes = [
  {
    title: 'On-time cloud migrations',
    text: 'Certified architects and engineers who execute complex migrations without costly delays.',
  },
  {
    title: 'Lower total cloud cost',
    text: 'FinOps-aware professionals who design and govern cost-efficient cloud environments.',
  },
  {
    title: 'More secure cloud environments',
    text: 'Security-first cloud engineers who eliminate misconfigurations before they become incidents.',
  },
  {
    title: 'Faster DevOps delivery',
    text: 'Platform and DevOps engineers who accelerate your CI/CD pipeline velocity.',
  },
  {
    title: 'Scalable cloud talent bench',
    text: 'Pre-vetted professionals ready to deploy as your cloud footprint expands.',
  },
];

const CloudStaffHome = () => {
    return (
        <div>
            <Helmet>
                <title>Cloud Staffing Solutions | AWS, Azure & GCP Certified Experts | Kapil Technologies</title>
                <meta name="description" content="Kapil Technologies provides certified cloud staffing solutions. Hire AWS, Azure, and GCP architects, DevOps engineers, and cloud security specialists." />
                <meta name="keywords" content="cloud staffing, AWS consultants, Azure developers, GCP experts, DevOps staffing, cloud migration specialists, infrastructure as code, Kapil Technologies cloud" />
            </Helmet>

            <TechIntro 
                videoUrl="https://res.cloudinary.com/djaejg3nn/video/upload/v1781602763/Kapil_Tech_Cloud_Staffing_Page_r27clz.mp4"
                titleWhite="AWS, Azure & GCP Certified Talent."
                titleGradient="Infrastructure Built Right."
            />

            <TechWhyChoose 
                title="Can't Find Certified Cloud Engineers for Your Migration or Build?"
                description="Kapil Tech connects enterprises with pre-vetted cloud professionals — from AWS solutions architects and Azure DevOps engineers to GCP data engineers and FinOps specialists. Whether you're migrating, modernizing, or building cloud-native, we deliver the talent that makes it happen."
                stats={[
                    { value: "400+", label: "Cloud Experts" },
                    { value: "96%", label: "Client Satisfaction Rate" }
                ]}
                imageUrl="https://ik.imagekit.io/7wqj9br0b/KapilTech_Website_Staffing/Technology/Kapil_Tech_Cloud_Staffing.jpg"
                imageAlt="Cloud Staffing by Kapil Tech"
            />

            <TechRoles 
                title="Cloud Roles & Specializations We Staff"
                description="Our cloud talent network spans every discipline of modern cloud infrastructure and operations."
                roleGroups={cloudRoleGroups}
            />

            <TechStaffingServices 
                title="Why Enterprises Choose Kapil Tech for Cloud Staffing"
                description="We combine deep cloud domain expertise with an agile staffing model to deliver top-tier talent at speed."
                features={cloudFeatures}
            />

            <TechRecruitingAgency 
                title="Is a Cloud Talent Shortage Delaying Your Modernization Journey?"
                description="Cloud transformation projects are among the most complex and high-stakes IT initiatives an organization can undertake. Generalist staffing agencies routinely misrepresent cloud expertise — leading to misconfigurations, security vulnerabilities, cost overruns, and failed migrations that set businesses back by months or years."
                problemTitle="The Real Cost of the Wrong Cloud Hire"
                problemItems={cloudProblemItems}
                solutionTitle="Kapil Tech Solution :"
                solutionDescription="Kapil Tech operates exclusively within the technology talent ecosystem, with a focused Cloud practice staffed by recruiters who understand the difference between a cloud architect, a DevOps engineer, and a platform engineer — ensuring your cloud initiatives are built on a foundation of real expertise."
                solutionPoints={cloudSolutionPoints}
                ctaText="Find Your Cloud Talent Today"
            />

            <TechOutcomes 
                title="Business Outcomes You Can Expect"
                description="Partner with Kapil Tech to accelerate your cloud initiatives and achieve measurable business results through our expert talent solutions."
                outcomes={cloudOutcomes}
            />

            <TechCTA 
                title="Ready to Hire Top"
                titleGradient="Cloud Experts?"
                description="Share your cloud staffing requirements today. We respond within hours with certified AWS, Azure, and GCP professionals matched to your environment."
            />
        </div>
    );
};

export default CloudStaffHome;
